import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sparkles } from 'lucide-react';

// Variant 14: Vertical Waterfall — values drop down like a waterfall, merge at bottom
const seedValues = [1, 3, 5, 8, 12, 14, 16];
const winsValues = [1, 2, 3, 4, 5, 6];
const sharesValues = [10, 25, 50, 100, 200];
const CYCLE = 8500;

export const HeroGraphic: React.FC = () => {
    const [cycleIdx, setCycleIdx] = useState(0);
    const [showResult, setShowResult] = useState(false);
    const [counter, setCounter] = useState(0);

    const seed = seedValues[cycleIdx % seedValues.length];
    const wins = winsValues[cycleIdx % winsValues.length];
    const shares = sharesValues[cycleIdx % sharesValues.length];
    const total = seed * wins * shares;

    const droplets = useMemo(() =>
        Array.from({ length: 18 }).map((_, i) => ({
            id: i,
            col: i % 3,
            x: (Math.random() - 0.5) * 30,
            delay: 0.6 + i * 0.06,
            size: 4 + Math.random() * 5,
        })), [cycleIdx]);

    useEffect(() => {
        const interval = setInterval(() => {
            setShowResult(false);
            setCycleIdx((p) => p + 1);
        }, CYCLE);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowResult(true);
            let c = 0;
            const step = Math.max(1, Math.floor(total / 25));
            const ci = setInterval(() => {
                c += step;
                if (c >= total) { setCounter(total); clearInterval(ci); }
                else setCounter(c);
            }, 20);
            return () => clearInterval(ci);
        }, 2750);
        return () => clearTimeout(timer);
    }, [cycleIdx, total]);

    const cols = [
        { label: 'Seed', value: `#${seed}`, color: '#3b82f6', textClass: 'text-blue-400' },
        { label: 'Wins', value: `${wins}W`, color: '#f59e0b', textClass: 'text-orange-400' },
        { label: 'Invested', value: `$${shares}`, color: '#22c55e', textClass: 'text-green-400' },
    ];

    return (
        <div className="relative w-full h-[550px] flex flex-col items-center justify-center px-4 overflow-hidden">
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-96 h-96 bg-purple-500/5 rounded-full blur-[120px]" />

            <div className="text-xs uppercase tracking-[0.2em] text-slate-600 font-bold mb-3">
                The Points Formula
            </div>

            {/* $1000 budget header */}
            <motion.div
                className="mb-8 flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
            >
                <Sparkles className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-bold text-emerald-400">$1,000 Starting Budget</span>
            </motion.div>

            {/* Three source columns with × signs between */}
            <div className="flex items-center gap-5 sm:gap-8">
                {cols.map((col, i) => (
                    <React.Fragment key={col.label}>
                        <div className="flex flex-col items-center">
                            <span className="text-[10px] uppercase tracking-widest text-slate-600 font-bold mb-3">{col.label}</span>
                            <motion.div
                                className="w-[100px] h-[100px] rounded-2xl border border-slate-700/50 flex items-center justify-center"
                                style={{ backgroundColor: `${col.color}10` }}
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                            >
                                <AnimatePresence mode="wait">
                                    <motion.span
                                        key={`${cycleIdx}-${col.value}`}
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        className={`text-3xl font-black ${col.textClass} tabular-nums`}
                                    >
                                        {col.value}
                                    </motion.span>
                                </AnimatePresence>
                            </motion.div>
                        </div>

                        {/* × sign between columns */}
                        {i < 2 && (
                            <span className="text-2xl font-light text-slate-500 mt-6 select-none">&times;</span>
                        )}
                    </React.Fragment>
                ))}
            </div>

            {/* Waterfall zone */}
            <div className="relative w-[400px] h-[80px] my-2">
                {droplets.map((d) => {
                    const xPos = d.col === 0 ? 18 : d.col === 1 ? 50 : 82;
                    return (
                        <motion.div
                            key={`${cycleIdx}-${d.id}`}
                            className="absolute rounded-full"
                            style={{
                                width: d.size,
                                height: d.size,
                                left: `${xPos}%`,
                                top: 0,
                                backgroundColor: cols[d.col].color,
                            }}
                            initial={{ y: 0, opacity: 0, x: d.x }}
                            animate={{
                                y: [0, 35, 75],
                                opacity: [0, 0.8, 0],
                                x: [d.x, d.x * 0.3, 0],
                            }}
                            transition={{ duration: 0.8, delay: d.delay, ease: 'easeIn' }}
                        />
                    );
                })}
            </div>

            {/* Equals bar */}
            <motion.div
                className="w-[280px] h-[2px] bg-gradient-to-r from-blue-500/30 via-orange-500/30 to-green-500/30 rounded-full mb-4"
                animate={{ opacity: showResult ? 1 : 0.2 }}
            />

            {/* Result — fixed height container so layout never shifts */}
            <div className="h-[130px] flex flex-col items-center justify-center">
                <AnimatePresence mode="wait">
                    {showResult ? (
                        <motion.div
                            key={`r-${cycleIdx}`}
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="text-center"
                        >
                            <div className="text-sm font-mono text-slate-500 mb-2">
                                <span className="text-blue-400">#{seed}</span>
                                <span className="text-slate-600 mx-2">&times;</span>
                                <span className="text-orange-400">{wins}W</span>
                                <span className="text-slate-600 mx-2">&times;</span>
                                <span className="text-green-400">${shares}</span>
                            </div>
                            <motion.div
                                className="text-6xl sm:text-7xl font-black tabular-nums bg-gradient-to-b from-white to-slate-400 bg-clip-text text-transparent"
                                animate={{ scale: [1, 1.12, 1] }}
                                transition={{ duration: 0.4 }}
                            >
                                {counter.toLocaleString()}
                            </motion.div>
                            <div className="text-sm text-slate-500 mt-2">points earned</div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="placeholder"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.4 }}
                            exit={{ opacity: 0 }}
                            className="text-sm text-slate-700"
                        >
                            Calculating...
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default HeroGraphic;
